import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LitPage } from '../components/lit-page';
import { isPuzzleId, type PuzzleId } from '../components/luna-common';
import { navigate } from '../navigation';
import { getCurrentUser } from '../auth/session';
import { get } from '../utils/http';
import { getPuzzles } from '../puzzles/puzzleBase';
import initialisePuzzleMetadata from '../puzzles/puzzleMetadata';
import { encodePresetOptions } from '../puzzles/preset';
import type { Options } from '../puzzles/wasmBridge/viewModel';
import type { Programme } from '../types';

import '../components/luna-game-card/luna-game-card';
import '../components/luna-status-chip/luna-status-chip';

interface ProgrammeActivity {
  puzzle: PuzzleId;
  name: string;
  description: string;
  level: number;
  levelName: string;
  completed: boolean;
  options?: Options[];
  label?: string;
}

@customElement('app-programme')
export class AppProgramme extends LitPage {
  @state() private _activities: ProgrammeActivity[] = [];
  @state() private _loading = true;
  @state() private _stacked = false;

  private _levels = new Map<string, { name: string; id: number }[]>();
  private _mq: MediaQueryList | null = null;
  private _onMq = () => {
    this._stacked = this._mq?.matches ?? false;
  };

  connectedCallback() {
    super.connectedCallback();
    if (typeof window.matchMedia === 'function') {
      this._mq = window.matchMedia('(max-width: 1279px)');
      this._onMq();
      this._mq.addEventListener('change', this._onMq);
    }
  }

  disconnectedCallback() {
    this._mq?.removeEventListener('change', this._onMq);
    super.disconnectedCallback();
  }

  async firstUpdated() {
    const user = getCurrentUser();
    const email = user?.emails?.[0]?.email;
    if (!email) {
      this._loading = false;
      return;
    }

    try {
      const response = await get<Programme>(`participant/${encodeURIComponent(email)}/programme`);
      if (response.error || !response.result) {
        this._loading = false;
        return;
      }

      const puzzles = await getPuzzles();
      await initialisePuzzleMetadata(this._levels);

      this._activities = response.result.activities
        .filter((activity) => isPuzzleId(activity.puzzle))
        .map((activity) => {
          const puzzle = puzzles.find((model) => model.name.toLowerCase() === activity.puzzle)!;
          const levelName =
            activity.label ??
            (this._levels.get(activity.puzzle) ?? []).find((level) => level.id === activity.level)
              ?.name ??
            String(activity.level);
          return {
            puzzle: activity.puzzle as PuzzleId,
            name: puzzle?.name ?? activity.puzzle,
            description: puzzle?.description ?? '',
            level: activity.level,
            levelName,
            completed: activity.completed,
            options: activity.options,
            label: activity.label,
          };
        });
    } catch {
      this._loading = false;
      return;
    }

    this._loading = false;
  }

  private onPlay(activity: ProgrammeActivity): void {
    if (activity.options) {
      navigate(
        `/puzzles/${activity.puzzle}?custom=${encodePresetOptions(activity.options)}&label=${encodeURIComponent(activity.label ?? activity.levelName)}&from=programme`
      );
      return;
    }
    navigate(`/puzzles/${activity.puzzle}/${activity.level}?from=programme`);
  }

  content() {
    return html`
      <main class="app-programme">
        <h1 class="app-programme__heading mbw-type-title-screen">My programme</h1>

        ${this._loading
          ? ''
          : this._activities.length === 0
            ? html`<p class="app-programme__empty mbw-type-body-small"
                >Your programme activities will appear here.</p
              >`
            : html`
                <section class="app-programme__list" aria-label="My programme">
                  ${this._activities.map(
                    (activity) => html`
                      <luna-game-card
                        puzzle=${activity.puzzle}
                        layout=${this._stacked ? 'stacked' : 'horizontal'}
                        ?hide-play=${activity.completed}
                        @luna-play=${() => this.onPlay(activity)}
                      >
                        <span slot="title">${activity.name}</span>
                        <span class="app-programme__card-copy">
                          ${activity.description}
                          <span class="app-programme__level"
                            ><strong>Level:</strong>&nbsp;${activity.levelName}</span
                          >
                          ${activity.completed
                            ? html`<luna-status-chip label="Completed"></luna-status-chip>`
                            : ''}
                        </span>
                      </luna-game-card>
                    `
                  )}
                </section>
              `}
      </main>
    `;
  }

  styles() {
    return css`
      .app-programme {
        box-sizing: border-box;
        padding: 34px 34px 40px;
      }

      .app-programme__heading {
        margin: 0 0 24px;
        color: var(--mbw-text-ink);
      }

      .app-programme__list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .app-programme__empty {
        margin: 0;
        color: var(--mbw-text-body);
      }

      .app-programme__list luna-game-card {
        width: 100%;
      }

      /* Programme cards carry extra copy (level + status) and grow with it. */
      .app-programme__list .luna-game-card--horizontal {
        height: auto;
        min-height: 214px;
      }

      .app-programme__list .luna-game-card--stacked {
        height: auto;
        min-height: 397px;
      }

      .app-programme__card-copy {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .app-programme__level {
        color: var(--mbw-text-body);
      }

      @media (max-width: 1279px) {
        .app-programme {
          padding: 28px 24px 34px;
        }
      }

      @media (max-width: 767px) {
        .app-programme {
          padding: 16px 16px 24px;
        }
      }
    `;
  }
}
